/**
 * Subida de archivos al storage account por signed URL.
 *
 * Cada formulario pide su propia SAS (getSignedUrl / getSignedUrlMediaStorage /
 * getSignedUrlApplicant, segun el container que le corresponda) y despues llama
 * a estos helpers para el PUT. La parte que se compartia por copy-paste era
 * justamente esta, y por eso cuatro formularios terminaron reconstruyendo la
 * URL final contra otro host: el archivo subia bien y el enlace guardado no
 * resolvia. La regla vive aca y en un solo lugar.
 */

const BLOB_HEADERS = { "x-ms-blob-type": "BlockBlob" } as const;
const DEFAULT_CONTENT_TYPE = "application/octet-stream";

/** Quita el token SAS y deja la URL publica del blob. */
export const publicUrlFromSignedUrl = (signedUrl: string): string =>
  signedUrl.split("?")[0];

/**
 * Sube el archivo a la signed URL y devuelve la URL publica del blob.
 * Nunca armes esa URL a mano a partir del path: usa lo que devuelve el PUT.
 */
export async function putToSignedUrl(
  signedUrl: string,
  file: Blob,
  contentType?: string
): Promise<string> {
  const response = await fetch(signedUrl, {
    method: "PUT",
    headers: {
      ...BLOB_HEADERS,
      "Content-Type": contentType || file.type || DEFAULT_CONTENT_TYPE
    },
    body: file
  });

  if (!response.ok) {
    throw new Error(`Upload failed ${response.status}`);
  }

  return publicUrlFromSignedUrl(response.url);
}

/**
 * Igual que putToSignedUrl pero con progreso, que fetch no expone.
 * XHR no da la URL final, asi que se deriva de la signed URL: es la misma.
 */
export function putToSignedUrlWithProgress(
  signedUrl: string,
  file: Blob,
  onProgress?: (percent: number) => void,
  options?: { contentType?: string; timeoutMs?: number }
): Promise<string> {
  const { contentType, timeoutMs } = options ?? {};

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    if (timeoutMs) {
      xhr.timeout = timeoutMs;
    }

    xhr.upload.addEventListener("progress", (event) => {
      if (event.lengthComputable && onProgress) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    });

    xhr.addEventListener("load", () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(publicUrlFromSignedUrl(signedUrl));
      } else {
        reject(new Error(`El servidor respondió con error ${xhr.status}`));
      }
    });

    xhr.addEventListener("error", () =>
      reject(new Error("Error de red al subir el archivo"))
    );
    xhr.addEventListener("timeout", () =>
      reject(new Error("Timeout al subir el archivo"))
    );

    xhr.open("PUT", signedUrl);
    xhr.setRequestHeader("x-ms-blob-type", "BlockBlob");
    xhr.setRequestHeader(
      "Content-Type",
      contentType || file.type || DEFAULT_CONTENT_TYPE
    );
    xhr.send(file);
  });
}

/**
 * Devuelve la storage key a partir de la URL publica de un blob, o null si el
 * valor no es una URL usable.
 *
 * La URL es https://<cuenta>.blob.core.windows.net/<container>/<key...>, asi que
 * la key es todo lo que sigue al primer segmento del path. El nombre del
 * container NO se asume: lo define AZURE_STORAGE_CONTAINER en el backend y no
 * vive en este repo. Antes esto se resolvia con url.split("nodrize-secure-cms/"),
 * que devolvia undefined en cuanto el container se llamara distinto.
 */
export function storageKeyFromUrl(url?: string | null): string | null {
  if (!url) return null;

  try {
    const segments = new URL(url).pathname.split("/").filter(Boolean);
    // [0] es el container; hace falta al menos un segmento mas para la key.
    if (segments.length < 2) return null;

    return decodeURIComponent(segments.slice(1).join("/"));
  } catch {
    return null;
  }
}
